import { useApolloClient } from '@apollo/client';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { GetTracesDocument } from '@gonfalon/observability-graphql/hooks';
import type { GetTracesQuery, GetTracesQueryVariables } from '@gonfalon/observability-graphql/operations';
import { SortDirection } from '@gonfalon/observability-graphql/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type UseSampleTraceParams = {
  antlrQuery?: string;
  spanName?: string | null;
  environmentKey?: string | null;
  enabled?: boolean;
  lookbackMs?: number;
};

const DEFAULT_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

const escapeAntlrValue = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

function buildSampleTraceQuery({
  antlrQuery,
  spanName,
  environmentKey,
}: Pick<UseSampleTraceParams, 'antlrQuery' | 'spanName' | 'environmentKey'>): string {
  const parts: string[] = [];
  if (antlrQuery) {
    parts.push(antlrQuery);
  }
  if (spanName) {
    parts.push(`span_name="${escapeAntlrValue(spanName)}"`);
  }
  if (environmentKey) {
    parts.push(`environment="${escapeAntlrValue(environmentKey)}"`);
  }
  return parts.join(' AND ');
}

export function useSampleTrace({
  antlrQuery,
  spanName,
  environmentKey,
  enabled = true,
  lookbackMs = DEFAULT_LOOKBACK_MS,
}: UseSampleTraceParams) {
  const { project } = useProjectContext();
  // Pull the Apollo client from the surrounding ObservabilityQueryProvider
  // (mounted in MetricSourceQueryBuilder) instead of standing up a private
  // ApolloClient — keeps a single client per provider, with the same
  // credentials/auth wiring as the rest of the observability graph queries.
  const apolloClient = useApolloClient();
  return useQuery({
    enabled: enabled && Boolean(project._id),
    queryKey: ['sampleTrace', project._id, antlrQuery, spanName, environmentKey, lookbackMs],
    queryFn: async () => {
      const now = Date.now();
      const result = await apolloClient.query<GetTracesQuery, GetTracesQueryVariables>({
        query: GetTracesDocument,
        variables: {
          project_id: project._id,
          direction: SortDirection.Desc,
          params: {
            query: buildSampleTraceQuery({ antlrQuery, spanName, environmentKey }),
            date_range: {
              start_date: new Date(now - lookbackMs).toISOString(),
              end_date: new Date(now).toISOString(),
            },
            sort: { column: 'timestamp', direction: SortDirection.Desc },
          },
          limit: 1,
        },
        fetchPolicy: 'network-only',
      });
      return result.data?.traces.edges?.[0]?.node ?? null;
    },
    // Hold the previous trace as `data` while a new fetch is in flight so the
    // currently-expanded row stays visible across a row switch. Consumers
    // discriminate "actually new data arrived" from "showing a placeholder"
    // via the `isPlaceholderData` flag.
    placeholderData: keepPreviousData,
  });
}
